"use client"

/**
 * "Why take this test" cards under the hero of the CH-only blood add-on test
 * pages (coeliac, heavy metals, trace elements): a heading and four cards,
 * the last one saying what the test cannot tell you (Sven, AHD-13640).
 *
 * Ported from the membership-first site's `BloodFindsCards`
 * (`_components/membership-first/pricing/pricing-v5-blood-sections.tsx`,
 * PR #3220) so the pages can go live before that site does. Copy lives in
 * the `tests` namespace: `{prefix}Heading`, `{prefix}{1-4}Lead|Rest`, the
 * same wording as that branch's `pricingV5.*Finds*` keys. This is the one
 * copy of the add-on cards: it renders on the current and the
 * membership-first site alike, so the branch's `AddonFindsCards` and its
 * `pricingV5` add-on keys go when the two meet.
 */

import Image from "next/image"
import { useTranslations } from "next-intl"
import {
  Bread,
  Drop,
  Fish,
  ForkKnife,
  Hourglass,
  House,
  MagnifyingGlass,
  Pill,
  ShieldCheck,
  Stethoscope,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react"
import { blobMediaUrl } from "@/lib/blob-url"

const BACKGROUND = blobMediaUrl(
  "/images/marketing/mri-finds/mri-finds-bg-20261002-v5.webp"
)

type FindsPrefix = "coeliacFinds" | "heavyMetalsFinds" | "traceElementsFinds"

/** Test slug → its cards' copy prefix and icons. */
const ADDON_FINDS: Record<
  string,
  { prefix: FindsPrefix; icons: readonly PhosphorIcon[] }
> = {
  "coeliac-blood-test": {
    prefix: "coeliacFinds",
    icons: [MagnifyingGlass, Drop, Bread, Stethoscope],
  },
  "heavy-metals-blood-test": {
    prefix: "heavyMetalsFinds",
    icons: [Fish, House, MagnifyingGlass, Hourglass],
  },
  "trace-elements-blood-test": {
    prefix: "traceElementsFinds",
    icons: [ShieldCheck, ForkKnife, Pill, Stethoscope],
  },
}

export function AddonFindsCards({ slug }: { slug: string }) {
  const t = useTranslations("tests")
  const finds = ADDON_FINDS[slug]
  if (!finds) return null
  const { prefix, icons } = finds
  const cards = icons.map((icon, i) => ({ icon, n: (i + 1) as 1 | 2 | 3 | 4 }))

  return (
    <section
      id="addon-finds"
      aria-labelledby="addon-finds-heading"
      className="relative isolate overflow-hidden pt-14 pb-16 md:pt-[84px] md:pb-24"
    >
      <Image
        src={BACKGROUND}
        alt=""
        fill
        sizes="100vw"
        quality={90}
        className="-z-10 object-cover"
      />
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <h2
          id="addon-finds-heading"
          className="type-display-sm mx-auto max-w-[734px] text-center font-medium text-balance text-neutral-900"
        >
          {t(`${prefix}Heading`)}
        </h2>
        <ul className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:mt-[78px] lg:grid-cols-4">
          {cards.map(({ icon: Icon, n }) => (
            <li
              key={n}
              className="rounded-[20px] border border-white bg-white/90 p-8"
            >
              <Icon aria-hidden size={48} className="text-neutral-900" />
              <p className="type-body-xl mt-8 font-medium text-pretty text-neutral-900/50">
                <span className="text-neutral-900">
                  {t(`${prefix}${n}Lead`)}
                </span>{" "}
                {t(`${prefix}${n}Rest`)}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
